/**
 * PeriodBarChart Component
 * 
 * Reusable bar chart component for displaying period-based statistics.
 * Used for users, containers, and websites created per period.
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { typography, surfaces, borders, textColors } from '../../design-system/tokens';
import { fillMissingPeriods, formatDate, formatAxisTick } from '../utils/chartHelpers';

const PeriodBarChart = ({
  title,
  data = [],
  dataKey = 'count',
  barColor = '#3b82f6',
  loading = false,
  error = null,
  yAxisLabel = 'Count',
  granularity = 'day',
  startDate = null,
  endDate = null,
}) => {
  const chartData = fillMissingPeriods(data, granularity, startDate, endDate);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-3 shadow-lg`}>
          <p className={`${typography.label} ${textColors.primary} mb-2`}>
            {formatDate(label, granularity)}
          </p>
          <p className={`${typography.bodySmall}`} style={{ color: barColor }}>
            {yAxisLabel}: {payload[0].value.toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  // Loading state
  if (loading) {
    return (
      <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
        <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>
          {title}
        </h3>
        <div className="flex items-center justify-center h-80">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
        <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>
          {title}
        </h3>
        <div className="flex flex-col items-center justify-center h-80 gap-3">
          <svg className="w-12 h-12 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className={`${typography.body} ${textColors.secondary}`}>
            Failed to load chart data
          </p>
          <p className={`${typography.bodySmall} ${textColors.muted}`}>
            {error.message || 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!chartData || chartData.length === 0) {
    return (
      <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
        <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>
          {title}
        </h3>
        <div className="flex flex-col items-center justify-center h-80 gap-3">
          <svg className={`w-12 h-12 ${textColors.muted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className={`${typography.body} ${textColors.secondary}`}>
            No data available for the selected period
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
      <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>
        {title}
      </h3>
      
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="currentColor" 
            className={`${textColors.muted} opacity-30`}
          />
          
          <XAxis
            dataKey="period"
            tickFormatter={(value) => formatDate(value, granularity)}
            className={`${textColors.tertiary} opacity-80`}
            stroke="currentColor"
            angle={-45}
            textAnchor="end"
            height={80}
          />
          
          <YAxis
            label={{ value: yAxisLabel, angle: -90, position: 'insideLeft' }}
            tickFormatter={formatAxisTick}
            className={`${textColors.tertiary} opacity-80`}
            stroke="currentColor"
          />
          
          <Tooltip content={<CustomTooltip />} />
          
          <Bar
            dataKey={dataKey}
            fill={barColor}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PeriodBarChart;

